/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import Vue from 'vue';

import { itertools } from '@codegrade/utils';

import client from '@/client';
import { PlagiarismCase } from '@/models/plagiarism';
import * as utils from '@/utils';

import { store } from '..';
import * as types from '../mutation-types';

const LIMIT_PER_REQUEST = 25;

const getters = {
    runs: state => state.runs,
};

// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function addUsersToStore(dispatch, cases) {
    return Promise.all(
        itertools.flatMap1(cases || [], c =>
            c.users
                .filter(u => u != null)
                .map(u => dispatch('users/addOrUpdateUser', { user: u }, { root: true })),
        ),
    );
}

const actions = {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    loadRun({ state, commit, dispatch }, runId) {
        if (state.loadRunPromises[runId] == null) {
            const promise = Promise.all([
                client.plagiarism.get({ plagiarismId: runId }).then(x => x.unsafeCoerce()),
                client.plagiarism
                    .getCases({
                        plagiarismId: runId,
                        offset: 0,
                        limit: LIMIT_PER_REQUEST,
                    })
                    .then(x => x.unsafeCoerce()),
            ]).then(async ([{ cgResult: run }, { cgResult: cases }]) => {
                run.cases = (cases || []).map(c => new PlagiarismCase(run, c));
                run.hasMoreCases = run.cases.length >= LIMIT_PER_REQUEST;
                await addUsersToStore(dispatch, run.cases);
                commit(types.SET_PLAGIARISM_RUN, run);
                return run;
            });
            commit(types.SET_PLAGIARISM_PROMISE, { runId, promise });
        }
        return state.loadRunPromises[runId];
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async loadMoreCases({ state, commit, dispatch }, runId) {
        await state.loadRunPromises[runId];
        const run = state.runs[runId];
        if (run.hasMoreCases) {
            const { cgResult: cases } = await client.plagiarism
                .getCases({
                    plagiarismId: runId,
                    offset: state.runs[runId].cases.length,
                    limit: LIMIT_PER_REQUEST,
                })
                .then(x => x.unsafeCoerce());
            const newCases = cases.map(c => new PlagiarismCase(run, c));
            await addUsersToStore(dispatch, newCases);
            commit(types.ADD_PLAGIARISM_CASES, {
                runId,
                newCases,
            });
        }
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async refreshRun({ dispatch }, runId) {
        await dispatch('removeRun', runId);
        return dispatch('loadRun', runId);
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    removeRun({ commit }, runId) {
        commit(types.CLEAR_PLAGIARISM_RUN, runId);
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    clear({ commit }) {
        commit(types.CLEAR_PLAGIARISM_RUNS);
    },
};

const mutations = {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    [types.SET_PLAGIARISM_RUN](state, run) {
        Vue.set(state.runs, run.id, run || {});
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    [types.CLEAR_PLAGIARISM_RUN](state, runId) {
        Vue.set(state.runs, runId, undefined);
        Vue.set(state.loadRunPromises, runId, undefined);

        delete state.runs[runId];
        delete state.loadRunPromises[runId];
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    [types.ADD_PLAGIARISM_CASES](state, { runId, newCases }) {
        const run = state.runs[runId];
        run.cases = run.cases.concat(newCases);
        run.hasMoreCases = newCases.length >= LIMIT_PER_REQUEST;
        Vue.set(state.runs, runId, run);
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    [types.SET_PLAGIARISM_PROMISE](state, { runId, promise }) {
        Vue.set(state.loadRunPromises, runId, promise);
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    [types.CLEAR_PLAGIARISM_RUNS](state) {
        state.runs = {};
        state.loadRunPromises = {};
    },
};

export default {
    namespaced: true,
    state: {
        runs: {},
        loadRunPromises: {},
    },
    getters,
    actions,
    mutations,
};

utils.signal.AFTER_USER_LOGGED_OUT.register(() => {
    store.commit(`plagiarism/${types.CLEAR_PLAGIARISM_RUNS}`, null, { root: true });
});
